import * as THREE from 'three'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { MachineId } from '../core/types'
import type { MachineWorld } from '../physics/MachineWorld'

function makeBallTexture(number: number, color: 'red' | 'blue'): THREE.CanvasTexture {
  const w = 1024
  const h = 512
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!

  const base = color === 'red' ? '#DE1F05' : '#1565C0'
  const deep = color === 'red' ? '#A30E02' : '#0D47A1'
  const g = ctx.createLinearGradient(0, 0, 0, h)
  g.addColorStop(0, deep)
  g.addColorStop(0.5, base)
  g.addColorStop(1, deep)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, w, h)

  const shade = ctx.createLinearGradient(0, 0, w, 0)
  shade.addColorStop(0, 'rgba(0,0,0,0.18)')
  shade.addColorStop(0.35, 'rgba(255,255,255,0.06)')
  shade.addColorStop(0.7, 'rgba(0,0,0,0.12)')
  shade.addColorStop(1, 'rgba(0,0,0,0.2)')
  ctx.fillStyle = shade
  ctx.fillRect(0, 0, w, h)

  const label = String(number)
  const patchR = Math.min(w, h) * 0.09
  const bands = [0.32, 0.68]
  const cols = 6
  for (const v of bands) {
    for (let i = 0; i < cols; i++) {
      const u = (i + (v < 0.5 ? 0 : 0.5)) / cols
      const cx = u * w
      const cy = v * h
      drawNumberPatch(ctx, cx, cy, patchR, label)
      if (cx + patchR > w) drawNumberPatch(ctx, cx - w, cy, patchR, label)
      if (cx - patchR < 0) drawNumberPatch(ctx, cx + w, cy, patchR, label)
    }
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  tex.wrapS = THREE.RepeatWrapping
  tex.needsUpdate = true
  return tex
}

function drawNumberPatch(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  label: string,
): void {
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = '#FFFFFF'
  ctx.fill()
  ctx.strokeStyle = 'rgba(0,0,0,0.08)'
  ctx.lineWidth = Math.max(1, r * 0.04)
  ctx.stroke()
  ctx.fillStyle = '#111111'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const fontSize = label.length > 1 ? r * 1.15 : r * 1.35
  ctx.font = `700 ${fontSize}px Arial Black, "Helvetica Neue", Arial, sans-serif`
  ctx.fillText(label, cx, cy + r * 0.04)
}

function makeBallMaterial(
  map: THREE.Texture,
  envMap: THREE.Texture | null,
): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    map,
    roughness: 0.18,
    metalness: 0.05,
    clearcoat: 1,
    clearcoatRoughness: 0.12,
    envMap,
    envMapIntensity: 0.85,
    reflectivity: 0.6,
  })
}

/** 直角三角形楔：直角在外侧，斜边朝向盘心 */
function createRightTriangleWedgeGeometry(
  radial: number,
  height: number,
  thickness: number,
): THREE.BufferGeometry {
  const shape = new THREE.Shape()
  shape.moveTo(0, 0)
  shape.lineTo(radial, 0)
  shape.lineTo(radial, height)
  shape.closePath()
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: thickness,
    bevelEnabled: false,
    steps: 1,
  })
  geo.translate(0, 0, -thickness / 2)
  return geo
}

function acrylicMaterial(envMap: THREE.Texture | null): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: '#ffffff',
    metalness: 0,
    roughness: 0.08,
    transmission: 0.88,
    thickness: 0.04,
    ior: 1.45,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    envMap,
    envMapIntensity: 0.6,
    side: THREE.DoubleSide,
    clearcoat: 0.4,
    clearcoatRoughness: 0.1,
  })
}

function chamberGlassMaterial(envMap: THREE.Texture | null): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: '#ffffff',
    metalness: 0,
    roughness: 0.02,
    transmission: 1,
    thickness: 0.015,
    ior: 1.4,
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
    envMap,
    envMapIntensity: 0.35,
    side: THREE.FrontSide,
    clearcoat: 0.6,
    clearcoatRoughness: 0.05,
    specularIntensity: 0.35,
  })
}

function alloyMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: '#6b7280',
    metalness: 0.88,
    roughness: 0.32,
  })
}

function accentChrome(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: '#d0d5dd',
    metalness: 0.95,
    roughness: 0.2,
  })
}

/**
 * 搅拌式外观：顶部落球管 + 透明球舱 + 同心双底盘（逆向）+ 底部出球竖管 + 展位轨
 */
export class LotteryRenderer {
  readonly scene = new THREE.Scene()
  readonly camera: THREE.PerspectiveCamera
  readonly renderer: THREE.WebGLRenderer
  private readonly root = new THREE.Group()
  private readonly ballMeshes = new Map<string, THREE.Mesh>()
  private readonly diskMeshes: THREE.Object3D[] = []
  private boundMachine: MachineId | null = null
  private envMap: THREE.Texture | null = null
  private lookAt = new THREE.Vector3(0, 0.55, 0.08)
  private camBase = new THREE.Vector3(1.35, 1.0, 2.05)
  /** 绑定机台后用于竖/横屏构图的注视点（舱心偏下含 U 管） */
  private frameFocus = new THREE.Vector3(0, 0.55, 0.08)
  private readonly controls: OrbitControls
  /** 用户拖过视角后，resize 只更新焦点不强制复位相机 */
  private userOrbited = false

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.camera = new THREE.PerspectiveCamera(36, 1, 0.05, 40)
    this.camera.position.copy(this.camBase)

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
    })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.08
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap

    this.scene.background = new THREE.Color('#1a2332')
    this.scene.add(this.root)

    this.controls = new OrbitControls(this.camera, this.canvas)
    this.controls.enablePan = false
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.08
    this.controls.rotateSpeed = 0.85
    this.controls.zoomSpeed = 0.9
    this.controls.minDistance = 1.05
    this.controls.maxDistance = 3.6
    this.controls.minPolarAngle = Math.PI * 0.18
    this.controls.maxPolarAngle = Math.PI * 0.48
    this.controls.target.copy(this.lookAt)
    this.controls.addEventListener('start', () => {
      this.userOrbited = true
    })

    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    this.scene.environment = this.envMap
    pmrem.dispose()

    const hemi = new THREE.HemisphereLight('#e8eef8', '#2a3344', 0.9)
    this.scene.add(hemi)
    const key = new THREE.DirectionalLight('#ffffff', 2.0)
    key.position.set(2.5, 4.5, 3)
    key.castShadow = true
    key.shadow.mapSize.set(1024, 1024)
    this.scene.add(key)
    const fill = new THREE.PointLight('#9ec5ff', 0.55, 8)
    fill.position.set(-1.5, 1.2, 1.2)
    this.scene.add(fill)

    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(2.6, 64),
      new THREE.MeshStandardMaterial({
        color: '#121820',
        metalness: 0.4,
        roughness: 0.55,
      }),
    )
    floor.rotation.x = -Math.PI / 2
    floor.receiveShadow = true
    this.scene.add(floor)

    window.addEventListener('resize', () => this.resize())
    this.resize()
  }

  resize(): void {
    const parent = this.canvas.parentElement ?? document.body
    const w = parent.clientWidth
    const h = parent.clientHeight
    this.camera.aspect = w / Math.max(1, h)
    this.applyCameraFraming(false)
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(w, h, false)
  }

  /** 竖屏拉近、加大 FOV；横屏保持桌面构图。仅视觉，不影响物理。 */
  private applyCameraFraming(forceReset: boolean): void {
    const portrait = this.camera.aspect < 0.85
    const fy = this.frameFocus.y
    const fz = this.frameFocus.z
    if (portrait) {
      this.camera.fov = 52
      this.camBase.set(0.95, fy + 0.28, 1.55)
      this.lookAt.set(0, fy - 0.02, fz)
      this.controls.minDistance = 1.0
      this.controls.maxDistance = 2.8
    } else {
      this.camera.fov = 36
      this.camBase.set(1.3, fy + 0.42, 1.95)
      this.lookAt.set(0, fy + 0.05, fz)
      this.controls.minDistance = 1.2
      this.controls.maxDistance = 3.6
    }
    this.controls.target.copy(this.lookAt)
    if (forceReset || !this.userOrbited) {
      this.camera.position.copy(this.camBase)
      this.controls.update()
    }
  }

  /** 保留接口；红球结果由底部 HUD 展示，3D 场景不再钉红球 */
  setPinnedResults(_red: number[], _blue: number[]): void {}

  bindWorld(world: MachineWorld): void {
    if (this.boundMachine === world.machineId && this.ballMeshes.size) {
      this.sync(world)
      return
    }
    this.clearMachine()
    this.boundMachine = world.machineId
    const color = world.machineId === 'red' ? 'red' : 'blue'
    const layout = world.handles.layout
    const R = layout.chamberRadius
    const cy = layout.chamberCenterY
    const glass = acrylicMaterial(this.envMap)
    const chamberGlass = chamberGlassMaterial(this.envMap)
    const alloy = alloyMaterial()
    const chrome = accentChrome()

    // 底座外宽 750mm
    const base = new THREE.Group()
    const cabinetH = layout.baseHeight
    const baseR = layout.baseOuterRadius
    const cabinet = new THREE.Mesh(
      new THREE.CylinderGeometry(baseR * 0.92, baseR, cabinetH, 48),
      alloy,
    )
    cabinet.position.y = cabinetH / 2
    cabinet.castShadow = true
    base.add(cabinet)

    // 转盘承托面 / 外圈：透明玻璃，不再用蓝色底板
    const diskBaseGlass = glass.clone()
    diskBaseGlass.opacity = 0.35
    diskBaseGlass.transmission = 0.9
    diskBaseGlass.color = new THREE.Color('#eef6ff')
    const basePlate = new THREE.Mesh(
      new THREE.CircleGeometry(layout.outerDiskOuterR, 64),
      diskBaseGlass,
    )
    basePlate.rotation.x = -Math.PI / 2
    basePlate.position.y = world.handles.diskTopY + 0.001
    base.add(basePlate)

    if (layout.floorRadius > layout.outerDiskOuterR + 0.01) {
      const floorRingMat = glass.clone()
      floorRingMat.opacity = 0.28
      floorRingMat.transmission = 0.88
      const floorRing = new THREE.Mesh(
        new THREE.RingGeometry(layout.outerDiskOuterR + 0.002, layout.floorRadius, 64),
        floorRingMat,
      )
      floorRing.rotation.x = -Math.PI / 2
      floorRing.position.y = world.handles.diskTopY
      base.add(floorRing)
    }

    const collar = new THREE.Mesh(
      new THREE.TorusGeometry(layout.outerDiskOuterR, 0.006, 12, 64),
      glass.clone(),
    )
    collar.rotation.x = Math.PI / 2
    collar.position.y = world.handles.diskTopY + 0.002
    base.add(collar)

    // 透明支柱
    const pillarH = Math.max(0.08, layout.chamberBottomY - cabinetH)
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6
      const pillar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.016, 0.016, pillarH, 12),
        glass.clone(),
      )
      pillar.position.set(
        Math.cos(a) * layout.outerDiskOuterR * 0.85,
        cabinetH + pillarH / 2,
        Math.sin(a) * layout.outerDiskOuterR * 0.85,
      )
      base.add(pillar)
    }
    this.root.add(base)

    const innerLight = new THREE.PointLight('#ffffff', 1.35, R * 4, 2)
    innerLight.position.set(0, cy, 0)
    this.root.add(innerLight)

    // 球舱（透明玻璃，无赤道圈）
    const chamber = new THREE.Mesh(new THREE.SphereGeometry(R, 64, 48), chamberGlass)
    chamber.position.y = cy
    chamber.renderOrder = 2
    this.root.add(chamber)

    // 舱内落球管：与球舱同系玻璃（避免亚克力发白，看起来像舱外另装的架子）
    const tubeGlass = chamberGlass.clone()
    tubeGlass.side = THREE.DoubleSide
    tubeGlass.opacity = 0.26
    tubeGlass.thickness = 0.01
    tubeGlass.envMapIntensity = 0.28
    tubeGlass.clearcoat = 0.45

    const preview = new THREE.Group()
    preview.renderOrder = 1
    for (const tube of layout.feedTubes) {
      const h = tube.topY - tube.bottomY
      const mesh = new THREE.Mesh(
        new THREE.CylinderGeometry(tube.radius, tube.radius, h, 24, 1, true),
        tubeGlass.clone(),
      )
      mesh.position.set(tube.x, (tube.topY + tube.bottomY) / 2, tube.z)
      mesh.renderOrder = 1
      preview.add(mesh)
      // 管口细环：同玻璃，弱化“金属外架”感
      const rim = new THREE.Mesh(
        new THREE.TorusGeometry(tube.radius + 0.003, 0.0035, 8, 24),
        tubeGlass.clone(),
      )
      rim.rotation.x = Math.PI / 2
      rim.position.set(tube.x, tube.topY, tube.z)
      rim.renderOrder = 1
      preview.add(rim)
    }
    // 轻量玻璃横梁（连接舱顶管口）
    if (layout.feedTubes.length >= 2) {
      const first = layout.feedTubes[0]!
      const last = layout.feedTubes[layout.feedTubes.length - 1]!
      const beamLen = Math.abs(last.x - first.x) + 0.04
      const beam = new THREE.Mesh(
        new THREE.BoxGeometry(beamLen, 0.01, 0.012),
        tubeGlass.clone(),
      )
      beam.position.set(
        (first.x + last.x) / 2,
        first.topY + 0.018,
        first.z,
      )
      beam.renderOrder = 1
      preview.add(beam)
    }
    this.root.add(preview)

    // —— 同心双层平面转盘：透明有机玻璃质感 ——
    const diskAcrylic = new THREE.MeshPhysicalMaterial({
      color: '#e8f4ff',
      metalness: 0,
      roughness: 0.06,
      transmission: 0.92,
      thickness: 0.025,
      ior: 1.49,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      envMap: this.envMap,
      envMapIntensity: 0.55,
      side: THREE.DoubleSide,
      clearcoat: 0.55,
      clearcoatRoughness: 0.08,
    })
    const paddleAcrylic = diskAcrylic.clone()
    paddleAcrylic.opacity = 0.55
    paddleAcrylic.transmission = 0.85
    paddleAcrylic.color = new THREE.Color('#dceaf8')
    const rimAcrylic = diskAcrylic.clone()
    rimAcrylic.opacity = 0.65
    rimAcrylic.transmission = 0.7
    rimAcrylic.color = new THREE.Color('#c5d8ea')

    // 视觉盘顶对齐物理 diskTopY
    const diskVisualY = world.handles.diskTopY - 0.008
    const innerGroup = new THREE.Group()
    const innerDisk = new THREE.Mesh(
      new THREE.CylinderGeometry(layout.innerDiskRadius, layout.innerDiskRadius, 0.016, 48),
      diskAcrylic,
    )
    innerGroup.add(innerDisk)
    const seamRing = new THREE.Mesh(
      new THREE.TorusGeometry(layout.innerDiskRadius + 0.004, 0.003, 8, 48),
      rimAcrylic,
    )
    seamRing.rotation.x = Math.PI / 2
    seamRing.position.y = 0.009
    innerGroup.add(seamRing)

    // 内盘两个直角三角形搅拌楔
    const innerPaddleH = 0.028
    const innerPaddleRadial = 0.024
    const innerPaddleTangential = 0.016
    const innerPaddleR = layout.innerDiskRadius * 0.58
    const innerWedgeGeo = createRightTriangleWedgeGeometry(
      innerPaddleRadial,
      innerPaddleH,
      innerPaddleTangential,
    )
    for (let i = 0; i < 2; i++) {
      const a = (i / 2) * Math.PI * 2
      const paddle = new THREE.Mesh(innerWedgeGeo, paddleAcrylic)
      const r0 = innerPaddleR - innerPaddleRadial / 2
      paddle.position.set(Math.cos(a) * r0, 0.008, Math.sin(a) * r0)
      paddle.rotation.y = -a
      innerGroup.add(paddle)
    }

    innerGroup.position.set(0, diskVisualY, 0)
    this.root.add(innerGroup)
    this.diskMeshes.push(innerGroup)

    const outerGroup = new THREE.Group()
    const outerSlabA = new THREE.Mesh(
      new THREE.RingGeometry(layout.outerDiskInnerR, layout.outerDiskOuterR, 64),
      diskAcrylic.clone(),
    )
    outerSlabA.rotation.x = -Math.PI / 2
    outerGroup.add(outerSlabA)
    const outerSlabB = new THREE.Mesh(
      new THREE.RingGeometry(layout.outerDiskInnerR, layout.outerDiskOuterR, 64),
      diskAcrylic.clone(),
    )
    outerSlabB.rotation.x = -Math.PI / 2
    outerSlabB.position.y = 0.008
    outerGroup.add(outerSlabB)
    const outerEdge = new THREE.Mesh(
      new THREE.TorusGeometry(layout.outerDiskOuterR, 0.003, 8, 64),
      rimAcrylic.clone(),
    )
    outerEdge.rotation.x = Math.PI / 2
    outerEdge.position.y = 0.004
    outerGroup.add(outerEdge)
    const innerEdge = new THREE.Mesh(
      new THREE.TorusGeometry(layout.outerDiskInnerR, 0.0025, 8, 48),
      rimAcrylic.clone(),
    )
    innerEdge.rotation.x = Math.PI / 2
    innerEdge.position.y = 0.004
    outerGroup.add(innerEdge)

    // 外圈四个直角三角形搅拌楔
    const paddleH = 0.03
    const paddleRadial = 0.026
    const paddleTangential = 0.017
    const paddleR = (layout.outerDiskInnerR + layout.outerDiskOuterR) / 2
    const outerWedgeGeo = createRightTriangleWedgeGeometry(
      paddleRadial,
      paddleH,
      paddleTangential,
    )
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4
      const paddle = new THREE.Mesh(outerWedgeGeo, paddleAcrylic)
      const r0 = paddleR - paddleRadial / 2
      paddle.position.set(Math.cos(a) * r0, 0.008, Math.sin(a) * r0)
      paddle.rotation.y = -a
      outerGroup.add(paddle)
    }

    outerGroup.position.set(0, diskVisualY, 0)
    this.root.add(outerGroup)
    this.diskMeshes.push(outerGroup)

    // —— 底部：向左开口的 U 型出球管（球停在下轨左侧，无前方展位台）——
    const railGroup = new THREE.Group()
    const exit = layout.exitHoleCenter

    const holeRim = new THREE.Mesh(
      new THREE.TorusGeometry(layout.exitHoleRadius, 0.005, 10, 32),
      chrome.clone(),
    )
    holeRim.rotation.x = Math.PI / 2
    holeRim.position.set(exit.x, layout.chamberBottomY + 0.01, exit.z)
    railGroup.add(holeRim)

    // U 型透明出球管（与球舱同系玻璃）
    const pipePts = layout.railWaypoints.map(
      (p) => new THREE.Vector3(p.x, p.y, p.z),
    )
    if (pipePts.length >= 2) {
      const pipeGlass = chamberGlass.clone()
      pipeGlass.side = THREE.DoubleSide
      pipeGlass.opacity = 0.28
      pipeGlass.thickness = 0.012
      pipeGlass.envMapIntensity = 0.3
      const pipeCurve = new THREE.CatmullRomCurve3(pipePts, false, 'catmullrom', 0.35)
      const uPipe = new THREE.Mesh(
        new THREE.TubeGeometry(
          pipeCurve,
          Math.max(64, pipePts.length * 8),
          layout.exitShaftRadius,
          14,
          false,
        ),
        pipeGlass,
      )
      uPipe.renderOrder = 1
      railGroup.add(uPipe)
    }

    this.root.add(railGroup)

    for (const bundle of world.bundles) {
      const tex = makeBallTexture(bundle.entity.number, color)
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(world.config.ballRadius, 32, 24),
        makeBallMaterial(tex, this.envMap),
      )
      mesh.castShadow = true
      mesh.renderOrder = 1
      this.root.add(mesh)
      this.ballMeshes.set(bundle.entity.id, mesh)
    }

    // 对准球舱与 U 管下轨；换机台时复位视角
    this.frameFocus.set(
      0,
      layout.chamberCenterY * 0.42 + layout.railY * 0.35,
      layout.railZ * 0.4,
    )
    this.userOrbited = false
    this.applyCameraFraming(true)
    this.camera.updateProjectionMatrix()
    this.sync(world)
  }

  sync(world: MachineWorld): void {
    for (const bundle of world.bundles) {
      const mesh = this.ballMeshes.get(bundle.entity.id)
      if (!mesh) continue
      const p = bundle.body.position
      const q = bundle.body.quaternion
      mesh.position.set(p.x, p.y, p.z)
      mesh.quaternion.set(q.x, q.y, q.z, q.w)
    }
    const disks = [world.handles.diskInner, world.handles.diskOuter]
    disks.forEach((d, i) => {
      const mesh = this.diskMeshes[i]
      if (!mesh) return
      // 只同步旋转，高度锁在 diskTopY，避免视觉/物理错位
      mesh.position.y = world.handles.diskTopY - 0.008
      mesh.quaternion.set(d.quaternion.x, d.quaternion.y, d.quaternion.z, d.quaternion.w)
    })
  }

  private clearMachine(): void {
    while (this.root.children.length) {
      const obj = this.root.children.pop()!
      this.root.remove(obj)
      obj.traverse((c) => {
        if (c instanceof THREE.Mesh) {
          c.geometry.dispose()
          const m = c.material
          if (Array.isArray(m)) m.forEach((x) => x.dispose())
          else {
            if ('map' in m && m.map) (m.map as THREE.Texture).dispose()
            m.dispose()
          }
        }
      })
    }
    this.ballMeshes.clear()
    this.diskMeshes.length = 0
  }

  render(): void {
    this.controls.update()
    this.renderer.render(this.scene, this.camera)
  }

  dispose(): void {
    this.controls.dispose()
    this.clearMachine()
    this.envMap?.dispose()
    this.renderer.dispose()
  }
}
